import { observer } from 'mobx-react'
import React, { PropsWithChildren, useEffect, useRef } from 'react'

import { useGoogleMap } from '@react-google-maps/api'

import { MAPS_ZOOM_VALUES } from '../../constants'
import { MapsInstanceContextProvider } from '../../hooks/useMapsInstanceContext'
import { MapCoordinates, MapStyleEnum } from '../../types'

import './index.css'

interface Props extends PropsWithChildren {
   mapStyle: MapStyleEnum
   zoomValue: number
   isEditMode: boolean
   onChangeMapStyle: (mapStyle: MapStyleEnum) => void
   onChangeZoomValue: (zoomValue: number) => void
   setIsEditMode: (value: boolean) => void
}

const GoogleMapsContextProvider = (props: Props): React.ReactElement => {
   const { mapStyle, onChangeMapStyle, zoomValue, onChangeZoomValue } = props

   const mapInstance = useGoogleMap()

   const onClickMapRef = useRef<google.maps.MapsEventListener>()

   const initStreetView = () => {
      mapInstance?.getStreetView().setOptions({
         fullscreenControl: false
      })
   }

   useEffect(initStreetView, [])

   if (!mapInstance) return <></>

   const panToCoordinates = (coordinates: MapCoordinates): void => {
      mapInstance.panTo(coordinates)
      onChangeZoomValue(MAPS_ZOOM_VALUES.defaultZoom)
   }

   const fitBounds = (coordinatesList: MapCoordinates[]): void => {
      const bounds = new google.maps.LatLngBounds()
      coordinatesList.forEach(eachCoordinate => bounds.extend(eachCoordinate))
      mapInstance.fitBounds(bounds, 50)
   }

   const registerOnClick = (
      onClick: (coordinates: MapCoordinates) => void
   ): void => {
      const onClickMap = (mapEvent: google.maps.MapMouseEvent): void => {
         if (!mapEvent.latLng) return

         const newCoordinate = {
            lat: mapEvent.latLng.lat(),
            lng: mapEvent.latLng.lng()
         }

         onClick(newCoordinate)
      }

      onClickMapRef.current = mapInstance.addListener('click', onClickMap)
   }

   const unRegisterOnClick = (): void => {
      if (onClickMapRef.current)
         google.maps.event.removeListener(onClickMapRef.current)
   }

   return (
      <MapsInstanceContextProvider
         isEditMode={props.isEditMode}
         setIsEditMode={props.setIsEditMode}
         panTo={panToCoordinates}
         fitBounds={fitBounds}
         registerOnClick={registerOnClick}
         unRegisterOnClick={unRegisterOnClick}
         mapStyle={mapStyle}
         onChangeMapStyle={onChangeMapStyle}
         zoomValue={zoomValue}
         onChangeZoomValue={onChangeZoomValue}
         streetViewLayer={mapInstance.getStreetView()}
      >
         {props.children}
      </MapsInstanceContextProvider>
   )
}

export default observer(GoogleMapsContextProvider)
